/**
 * Bundled strings for the pre-authentication screens and offline starts.
 * The database seed is the source of truth for everything else.
 *
 * Keys are flat dotted strings, so i18next runs with `keySeparator: false` —
 * `t('common.save')` is one lookup, not a nested traversal.
 */
import AR_MESSAGES from './messages.ar.json';

export const EN_FALLBACK: Record<string, string> = {
  'app.name': 'Diwan',
  'common.save': 'Save',
  'common.cancel': 'Cancel',
  'common.copy': 'Copy',
  'col.department': 'Department',
  'col.shift': 'Shift',
  'col.geofence': 'Radius (m)',
  // --- employees whose requests have nowhere to go ---
  'approver_coverage.title': 'Employees with no approver',
  'approver_coverage.explainer':
    'Their requests are refused at submission with a named reason. The fix is usually one field — a branch with no manager covers everybody in it at once.',
  'approver_coverage.counts': '{{total}} employees cannot submit a request. {{named}} have somebody named who cannot approve; the rest have nobody named at all.',
  'approver_coverage.what_is_missing': 'What is missing',
  'approver_coverage.named_but_unusable': 'Named, but cannot approve',
  'approver_coverage.nobody_named': 'Nobody named',
  'approver_coverage.truncated': 'Showing {{shown}} of {{total}}. Export the report for the rest.',
  // --- employees no shift covers ---
  'shift_coverage.title': 'Employees without a shift',
  'shift_coverage.explainer':
    'Attendance is built by matching a punch to a shift window. With no shift these punches match nothing, so no attendance day is ever built — silently, until payroll.',
  'shift_coverage.assign': 'Assign a default shift',
  'shift_coverage.assign_count': 'Assigning a shift to {{count}} employee(s).',
  'shift_coverage.punches_30d': 'Punches (30d)',
  'shift_coverage.punching_warning':
    '{{count}} of these punched in the last 30 days and earned no attendance for it.',
  'common.done': 'Done',
  'common.close': 'Close',
  'common.delete': 'Delete',
  'common.edit': 'Edit',
  'common.create': 'Create',
  'common.search': 'Search',
  'common.all': 'All',
  // --- opening app accounts ---
  'provision.title': 'App accounts',
  'provision.explainer':
    'Opens employee app accounts for a branch and produces a sheet of initial passwords to hand over in person. The sheet is the only copy — the passwords cannot be shown again.',
  'provision.branch_hint': 'Leave empty to cover the whole company',
  'provision.pick_company': 'Choose a company first',
  'provision.preview': 'Preview',
  'provision.create_and_download': 'Create accounts and download sheet',
  'provision.will_create': 'Will create',
  'provision.will_skip': 'Will skip',
  'provision.skipped_heading': 'Not given an account',
  'provision.reason': 'Reason',
  'provision.no_mobile': 'No mobile number on the record',
  'provision.invalid_mobile': 'Mobile number cannot be read — needs correcting',
  'provision.create_account': 'Open app account',
  'provision.app_password': 'App password',
  'provision.not_created': 'No account was opened — check the employee record',
  'provision.duplicate_mobile': 'Number already belongs to another employee',
  'provision.already_has_user': 'Already has an account',
  'provision.reset_password': 'Reset app password',
  'provision.reset_explainer': 'Read this password out to the employee. It is shown once.',
  'provision.app_account': 'App account',
  'provision.no_account': 'No app account yet.',
  'provision.awaiting_first_change': 'Has not changed the issued password',
  'provision.never_signed_in': 'Never signed in',
  // --- the phone an employee signs in from ---
  'device.heading': 'Phone',
  'device.phone': 'Phone',
  'device.app_version': 'App version',
  'device.bound': 'Bound',
  'device.last_seen': 'Last seen',
  'device.unbind': 'Unbind',
  'device.unbound': 'Unbound',
  'device.unbound_by_manager': 'Unbound by the branch manager',
  'device.unbind_explainer':
    'The phone is signed out immediately and the employee cannot sign in again until they bind a new one. Use this when a handset is lost, replaced, or was someone else’s.',
  'device.reason': 'Reason',
  'device.reason_hint': 'Recorded against the employee, so this is answerable six months from now',
  'common.export': 'Export',
  'common.actions': 'Actions',
  'common.loading': 'Loading',
  'common.no_results': 'No results',
  'common.confirm_delete': 'Delete this record?',
  'common.language': 'Language',
  'nav.dashboard': 'Dashboard',
  'nav.approvals': 'Approvals',
  'nav.guide': 'Guide',
  'nav.people': 'People',
  'nav.employees': 'Employees',
  'nav.compliance': 'Document compliance',
  'nav.time': 'Time and attendance',
  'nav.attendance': 'Attendance',
  'nav.roster': 'Shift assignments',
  'nav.shift_assignments': 'Shift assignments',
  'nav.weekly_roster': 'Weekly roster',
  'nav.devices': 'Biometric devices',
  'nav.leave': 'Leave',
  'nav.expenses': 'Expenses and advances',
  'nav.salary_structures': 'Salary structures',
  'nav.payroll_settings': 'Payroll settings',
  'nav.benefits': 'Benefits and tax relief',
  'nav.lifecycle': 'Lifecycle',
  'nav.exit': 'End of service',
  'nav.pay': 'Payroll',
  'nav.payroll': 'Payroll runs',
  'nav.salary_slips': 'Salary slips',
  'nav.reports': 'Reports',
  'nav.talent': 'Talent',
  'nav.recruitment': 'Recruitment',
  'nav.applicants': 'Applicants and interviews',
  'nav.performance': 'Performance',
  'nav.training': 'Training',
  'nav.operations': 'Operations',
  'nav.fleet': 'Fleet',
  'nav.travel': 'Travel',
  'nav.grievances': 'Grievances',
  'nav.workplace': 'Workplace',
  'nav.organization': 'Organization',
  'nav.companies': 'Companies',
  'nav.branches': 'Branches',
  'nav.departments': 'Departments',
  'nav.designations': 'Designations',
  'nav.grades': 'Employee grades',
  'nav.employment_types': 'Employment types',
  'nav.administrations': 'Administrations',
  'nav.nationalities': 'Nationalities',
  'nav.holiday_lists': 'Holiday lists',
  'nav.access': 'Access',
  'nav.users': 'Users',
  'nav.roles': 'Roles',
  'nav.permissions': 'Permissions',
  'nav.system': 'System',
  'nav.jobs': 'Scheduled jobs',
  'auth.sign_in': 'Sign in',
  'auth.sign_out': 'Sign out',
  'auth.email': 'Email',
  'auth.password': 'Password',
  'auth.company': 'Company',
  'auth.invalid_credentials': 'Invalid email or password',
  'auth.signing_in': 'Signing in…',
  'workflow.pending': 'Pending approvals',
  'workflow.history': 'Approval history',
  'workflow.comment': 'Comment',
  'workflow.none_pending': 'Nothing is waiting for you',
  // Grid headers: DataTable resolves every headerName through t().
  'col.id': 'ID',
  'col.name': 'Name',
  'col.name_ar': 'Arabic name',
  'col.code': 'Code',
  'col.description': 'Description',
  'col.active': 'Active',
  'col.pin': 'Pin',
  'col.status': 'Status',
  'col.date': 'Date',
  'col.from': 'From',
  'col.to': 'To',
  'col.document': 'Document',
  'col.employee': 'Employee',
  'col.employee_name_ar': 'Arabic name',
  'col.employee_no': 'Employee no.',
  'col.national_id': 'National ID',
  'col.joined': 'Joined',
  'col.insurance': 'Insurance',
  'col.device_id': 'Device id',
  'col.hours': 'Hours',
  'col.late': 'Late',
  'col.late_minutes': 'Late (min)',
  'col.early_exit': 'Early exit',
  'col.ot_hours': 'OT hrs',
  'col.meal': 'Meal',
  'col.transport': 'Transport',
  'col.origin': 'Origin',
  'col.branch': 'Branch',
  'col.biometric_device': 'Biometric device',
  'col.meal_per_day': 'Meal / day',
  'col.transport_per_day': 'Transport / day',
  'col.variable_transport_per_day': 'Variable transport / day',
  'col.company': 'Company',
  'col.abbr': 'Abbr',
  'col.tax_card': 'Tax card',
  'col.commercial_register': 'Commercial reg.',
  'col.social_insurance': 'Social insurance',
  'col.payroll_cycle': 'Payroll cycle',
  'col.role': 'Role',
  'col.label': 'Label',
  'col.label_ar': 'Arabic label',
  'col.system': 'System',
  'col.desk_access': 'Desk access',
  'col.disabled': 'Disabled',
  'col.min_salary': 'Min salary',
  'col.max_salary': 'Max salary',
  'col.fixed_term': 'Fixed term',
  'col.iso': 'ISO',
  'col.egyptian': 'Egyptian',
  'col.work_permit': 'Work permit',
  'col.slip': 'Slip',
  'col.payment_days': 'Payment days',
  'col.gross': 'Gross',
  'col.deductions': 'Deductions',
  'col.net': 'Net',
  'col.employer_cost': 'Employer cost',
  'col.assignment': 'Assignment',
  'col.email': 'Email',
  'col.mobile': 'Mobile',
  'col.language': 'Language',
  'col.last_sign_in': 'Last sign-in',
  'col.roles': 'Roles',
  'auth.change_password': 'Change password',
  'auth.current_password': 'Current password',
  'auth.new_password': 'New password',
  'auth.confirm_password': 'Confirm new password',
  'auth.passwords_differ': 'The two passwords do not match',
  'auth.password_rule': 'At least {{count}} characters',
  'auth.sign_out_everywhere': 'Sign out everywhere else',
  'auth.set_password': 'Set password',
  'col.actions': 'Actions',
  'nav.data': 'Import and export',
  'nav.system_logs': 'System logs',
  'nav.notification_rules': 'Notification rules',
  'nav.workflows': 'Approval chains',
  'nav.statutory': 'Statutory tables',
  'nav.branding': 'Logo and colour',
  'col.overtime': 'Overtime',
  'col.gross_ytd': 'Gross YTD',
  'col.net_ytd': 'Net YTD',
  'col.warnings': 'Warnings',
  'col.in': 'In',
  'col.out': 'Out',
  'col.holiday': 'Holiday',
  'col.ctc': 'Cost to company',
};

const AR_BASE: Record<string, string> = {
  'app.name': 'ديوان',
  'common.save': 'حفظ',
  'common.cancel': 'إلغاء',
  'common.copy': 'نسخ',
  'col.department': 'القسم',
  'col.shift': 'الوردية',
  'col.geofence': 'نطاق الحضور (م)',
  // --- employees whose requests have nowhere to go ---
  'approver_coverage.title': 'موظفون بلا مسؤول اعتماد',
  'approver_coverage.explainer':
    'طلباتهم تُرفض عند الإرسال مع ذكر السبب. غالبًا الإصلاح حقل واحد — تعيين مدير للفرع يغطي كل من فيه دفعة واحدة.',
  'approver_coverage.counts': '{{total}} موظفًا لا يستطيعون تقديم طلب. {{named}} منهم لهم مسؤول معيّن لا يملك صلاحية الاعتماد، والباقي بلا مسؤول أصلًا.',
  'approver_coverage.what_is_missing': 'الناقص',
  'approver_coverage.named_but_unusable': 'معيّن، لكن لا يملك الصلاحية',
  'approver_coverage.nobody_named': 'لا يوجد معيّن',
  'approver_coverage.truncated': 'يُعرض {{shown}} من {{total}}. صدّر التقرير لبقية الأسماء.',
  // --- employees no shift covers ---
  'shift_coverage.title': 'موظفون بلا وردية',
  'shift_coverage.explainer':
    'يُبنى الحضور بمطابقة البصمة مع نافذة وردية. بدون وردية لا تطابق البصمات شيئًا، فلا يُنشأ يوم حضور إطلاقًا — دون أي خطأ، حتى يظهر في المرتبات.',
  'shift_coverage.assign': 'إسناد وردية افتراضية',
  'shift_coverage.assign_count': 'إسناد وردية لعدد {{count}} موظف.',
  'shift_coverage.punches_30d': 'البصمات (٣٠ يومًا)',
  'shift_coverage.punching_warning':
    '{{count}} منهم سجلوا بصمات خلال ٣٠ يومًا ولم يُحتسب لهم حضور.',
  'common.done': 'تم',
  'common.close': 'إغلاق',
  'common.delete': 'حذف',
  'common.edit': 'تعديل',
  'common.create': 'إنشاء',
  'common.search': 'بحث',
  'common.all': 'الكل',
  // --- opening app accounts ---
  'provision.title': 'حسابات التطبيق',
  'provision.explainer':
    'ينشئ حسابات التطبيق لموظفي فرع ويُصدر كشفًا بكلمات المرور المبدئية لتسليمها باليد. الكشف هو النسخة الوحيدة — لا يمكن عرض كلمات المرور مرة أخرى.',
  'provision.branch_hint': 'اتركه فارغًا لتشمل الشركة كلها',
  'provision.pick_company': 'اختر الشركة أولًا',
  'provision.preview': 'معاينة',
  'provision.create_and_download': 'إنشاء الحسابات وتنزيل الكشف',
  'provision.will_create': 'سيتم الإنشاء',
  'provision.will_skip': 'سيتم التخطي',
  'provision.skipped_heading': 'لم يتم إنشاء حساب لهم',
  'provision.reason': 'السبب',
  'provision.no_mobile': 'لا يوجد رقم موبايل في الملف',
  'provision.invalid_mobile': 'رقم الموبايل غير صالح — يحتاج تصحيح',
  'provision.create_account': 'فتح حساب التطبيق',
  'provision.app_password': 'كلمة مرور التطبيق',
  'provision.not_created': 'لم يُفتح حساب — راجع ملف الموظف',
  'provision.duplicate_mobile': 'الرقم مسجل لموظف آخر',
  'provision.already_has_user': 'لديه حساب بالفعل',
  'provision.reset_password': 'إعادة تعيين كلمة مرور التطبيق',
  'provision.reset_explainer': 'اقرأ كلمة المرور للموظف. تُعرض مرة واحدة فقط.',
  'provision.app_account': 'حساب التطبيق',
  'provision.no_account': 'لا يوجد حساب تطبيق بعد.',
  'provision.awaiting_first_change': 'لم يغيّر كلمة المرور المبدئية',
  'provision.never_signed_in': 'لم يسجل الدخول مطلقًا',
  // --- the phone an employee signs in from ---
  'device.heading': 'الهاتف',
  'device.phone': 'الهاتف',
  'device.app_version': 'إصدار التطبيق',
  'device.bound': 'تاريخ الربط',
  'device.last_seen': 'آخر ظهور',
  'device.unbind': 'فكّ الارتباط',
  'device.unbound': 'مفكوك',
  'device.unbound_by_manager': 'فكّه مدير الفرع',
  'device.unbind_explainer':
    'سيُسجَّل خروج الهاتف فورًا ولن يتمكن الموظف من الدخول حتى يربط هاتفًا جديدًا. استخدم هذا عند فقد الهاتف أو تغييره أو إن كان هاتف شخص آخر.',
  'device.reason': 'السبب',
  'device.reason_hint': 'يُسجَّل على ملف الموظف، ليكون مفهومًا بعد ستة أشهر',
  'common.export': 'تصدير',
  'common.actions': 'إجراءات',
  'common.loading': 'جارٍ التحميل',
  'common.no_results': 'لا توجد نتائج',
  'common.confirm_delete': 'هل تريد حذف هذا السجل؟',
  'common.language': 'اللغة',
  'nav.dashboard': 'الرئيسية',
  'nav.approvals': 'الموافقات',
  'nav.guide': 'الدليل',
  'nav.people': 'الموظفون',
  'nav.employees': 'الموظفون',
  'nav.compliance': 'متابعة المستندات',
  'nav.time': 'الوقت والحضور',
  'nav.attendance': 'الحضور',
  'nav.roster': 'إسناد الورديات',
  'nav.shift_assignments': 'إسناد الورديات',
  'nav.weekly_roster': 'جدول الأسبوع',
  'nav.devices': 'أجهزة البصمة',
  'nav.leave': 'الإجازات',
  'nav.expenses': 'المصروفات والسلف',
  'nav.salary_structures': 'هياكل الرواتب',
  'nav.payroll_settings': 'إعدادات الرواتب',
  'nav.benefits': 'المزايا والإعفاءات الضريبية',
  'nav.lifecycle': 'دورة حياة الموظف',
  'nav.exit': 'نهاية الخدمة',
  'nav.pay': 'الرواتب',
  'nav.payroll': 'تشغيل الرواتب',
  'nav.salary_slips': 'قسائم الرواتب',
  'nav.reports': 'التقارير',
  'nav.talent': 'المواهب',
  'nav.recruitment': 'التوظيف',
  'nav.applicants': 'المتقدمون والمقابلات',
  'nav.performance': 'الأداء',
  'nav.training': 'التدريب',
  'nav.operations': 'العمليات',
  'nav.fleet': 'المركبات',
  'nav.travel': 'السفر',
  'nav.grievances': 'التظلمات',
  'nav.workplace': 'بيئة العمل',
  'nav.organization': 'الهيكل التنظيمي',
  'nav.companies': 'الشركات',
  'nav.branches': 'الفروع',
  'nav.departments': 'الأقسام',
  'nav.designations': 'المسميات الوظيفية',
  'nav.grades': 'الدرجات الوظيفية',
  'nav.employment_types': 'أنواع التوظيف',
  'nav.administrations': 'الإدارات',
  'nav.nationalities': 'الجنسيات',
  'nav.holiday_lists': 'قوائم العطلات',
  'nav.access': 'الصلاحيات',
  'nav.users': 'المستخدمون',
  'nav.roles': 'الأدوار',
  'nav.permissions': 'الصلاحيات',
  'nav.system': 'النظام',
  'nav.jobs': 'المهام المجدولة',
  'auth.sign_in': 'تسجيل الدخول',
  'auth.sign_out': 'تسجيل الخروج',
  'auth.email': 'البريد الإلكتروني',
  'auth.password': 'كلمة المرور',
  'auth.company': 'الشركة',
  'auth.invalid_credentials': 'البريد الإلكتروني أو كلمة المرور غير صحيحة',
  'auth.signing_in': 'جارٍ تسجيل الدخول…',
  'workflow.pending': 'بانتظار الموافقة',
  'workflow.history': 'سجل الموافقات',
  'workflow.comment': 'ملاحظة',
  'workflow.none_pending': 'لا توجد طلبات بانتظارك',
  'col.id': 'المعرف',
  'col.name': 'الاسم',
  'col.name_ar': 'الاسم بالعربية',
  'col.code': 'الكود',
  'col.description': 'الوصف',
  'col.active': 'نشط',
  'col.pin': 'الموقع',
  'col.status': 'الحالة',
  'col.date': 'التاريخ',
  'col.from': 'من',
  'col.to': 'إلى',
  'col.document': 'المستند',
  'col.employee': 'الموظف',
  'col.employee_name_ar': 'الاسم بالعربية',
  'col.employee_no': 'رقم الموظف',
  'col.national_id': 'الرقم القومي',
  'col.joined': 'تاريخ التعيين',
  'col.insurance': 'التأمين',
  'col.device_id': 'رقم الجهاز',
  'col.hours': 'الساعات',
  'col.late': 'تأخير',
  'col.late_minutes': 'دقائق التأخير',
  'col.early_exit': 'انصراف مبكر',
  'col.ot_hours': 'ساعات إضافية',
  'col.meal': 'الوجبة',
  'col.transport': 'الانتقال',
  'col.origin': 'المصدر',
  'col.branch': 'الفرع',
  'col.biometric_device': 'جهاز البصمة',
  'col.meal_per_day': 'الوجبة لليوم',
  'col.transport_per_day': 'الانتقال لليوم',
  'col.variable_transport_per_day': 'الانتقال المتغير لليوم',
  'col.company': 'الشركة',
  'col.abbr': 'الاختصار',
  'col.tax_card': 'البطاقة الضريبية',
  'col.commercial_register': 'السجل التجاري',
  'col.social_insurance': 'التأمينات الاجتماعية',
  'col.payroll_cycle': 'دورة الرواتب',
  'col.role': 'الدور',
  'col.label': 'التسمية',
  'col.label_ar': 'التسمية بالعربية',
  'col.system': 'نظامي',
  'col.desk_access': 'صلاحية الدخول',
  'col.disabled': 'معطل',
  'col.min_salary': 'أقل راتب',
  'col.max_salary': 'أعلى راتب',
  'col.fixed_term': 'محدد المدة',
  'col.iso': 'أيزو',
  'col.egyptian': 'مصري',
  'col.work_permit': 'تصريح عمل',
  'col.slip': 'المفردة',
  'col.payment_days': 'أيام الاستحقاق',
  'col.gross': 'الإجمالي',
  'col.deductions': 'الاستقطاعات',
  'col.net': 'الصافي',
  'col.employer_cost': 'تكلفة صاحب العمل',
  'col.assignment': 'التكليف',
  'col.email': 'البريد الإلكتروني',
  'col.mobile': 'الموبايل',
  'col.language': 'اللغة',
  'col.last_sign_in': 'آخر دخول',
  'col.roles': 'الأدوار',
  'auth.change_password': 'تغيير كلمة المرور',
  'auth.current_password': 'كلمة المرور الحالية',
  'auth.new_password': 'كلمة المرور الجديدة',
  'auth.confirm_password': 'تأكيد كلمة المرور',
  'auth.passwords_differ': 'كلمتا المرور غير متطابقتين',
  'auth.password_rule': '{{count}} أحرف على الأقل',
  'auth.sign_out_everywhere': 'تسجيل الخروج من كل الأجهزة',
  'auth.set_password': 'تعيين كلمة المرور',
  'col.actions': 'إجراءات',
  'nav.data': 'الاستيراد والتصدير',
  'nav.system_logs': 'سجلات النظام',
  'nav.notification_rules': 'قواعد الإشعارات',
  'nav.workflows': 'مسارات الاعتماد',
  'nav.statutory': 'الجداول القانونية',
  'nav.branding': 'الشعار واللون',
  'col.overtime': 'إضافي',
  'col.gross_ytd': 'الإجمالي منذ بداية السنة',
  'col.net_ytd': 'الصافي منذ بداية السنة',
  'col.warnings': 'تنبيهات',
  'col.in': 'دخول',
  'col.out': 'خروج',
  'col.holiday': 'عطلة',
  'col.ctc': 'التكلفة الإجمالية',
};

/**
 * The full Arabic catalogue, keyed by the English text as it appears in the
 * code. Generated by `docs/i18n-build.mjs`; the database seed for the Wording
 * page is derived from the same file so HR overrides sit on top of it.
 */
export const AR_FALLBACK: Record<string, string> = { ...AR_MESSAGES, ...AR_BASE };
